import { DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { toast } from "react-toastify";
import { CommonDialog } from "../../../components/common/CommonDialog";
import {
  FormAutoComplete,
  FormButtonGroup,
  FormDateInput,
  FormMultipleAutoComplete,
  FormTextInput,
} from "../../../components/form";
import { oids } from "../../../configs/Settings";
import { useOrganizationContext } from "../../../contexts/OrganizationContext";
import { usePractitionerRoleContext } from "../../../contexts/PractitionerRoleContext";
import { useSessionContext } from "../../../contexts/SessionContext";
import { SendEmail } from "../../../services/gmail";
import {
  GetPractitioner,
  PutPractitioner,
  PutPractitionerRole,
  Validate,
} from "../../../services/fhir";
import { SearchCPF, toSerproNascimento } from "../../../services/scim";
import { HTTPResponseStatus } from "../../../utils/enum";
import { useFormatter } from "../../../utils/useFormatter";
import { useRegex } from "../../../utils/useRegex";

// Necessário para o parse estrito de datas em formatos como "DDMMYYYY".
dayjs.extend(customParseFormat);

type AssociationOption = {
  cnpjDigits: string;
  organizationAlias: string;
};

interface IProps {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  practitioners: any;
  associationOptions?: AssociationOption[];
  defaultAssociationCnpjDigits?: string;
}

const defaultValues = {
  email: "",
  identifier: "",
  birthDate: null as any,
  phone: "",
  associationCnpj: null as any,
  role: [] as any[],
};

function onlyDigits(value: unknown): string {
  if (value === null || value === undefined) return "";
  return String(value).replace(/\D/g, "");
}

function isFhirOrganization(value: unknown): value is fhir5.Organization {
  if (!value || typeof value !== "object") return false;
  return (value as { resourceType?: unknown }).resourceType === "Organization";
}

/**
 * Diz se o CPF retornado pelo Serpro (via BFF) está regular.
 *
 * Por que existe: antes o convite comparava só `situacao.descricao === "Regular"`.
 * Com a migração para o Serpro V3, essa comparação exata passou a falhar e o
 * convite caía no catch genérico, sem criar o Practitioner/PractitionerRole.
 *
 * Regras (alinhadas ao src-next `cpfLookupTransformers.ts`):
 * - `situacao.codigo === "0"` => regular.
 * - `situacao.descricao` igual a "regular" (sem diferenciar maiúsculas) => regular.
 * - Sem bloco `situacao`, mas com `nome`: o BFF já validou CPF + nascimento
 *   (status 200), então tratamos como regular — mesmo critério usado pelos
 *   outros fluxos V3 do legado (ex.: pronto atendimento, dependentes).
 */
function isSerproCpfRegular(serproData: any): boolean {
  const situacao = serproData?.situacao;
  if (situacao) {
    if (String(situacao?.codigo ?? "").trim() === "0") return true;
    return String(situacao?.descricao ?? "").trim().toLowerCase() === "regular";
  }
  return Boolean(serproData?.nome);
}

/**
 * Converte a data de nascimento para o formato FHIR ("YYYY-MM-DD").
 *
 * Por que existe: o antigo `transformData` cortava a string assumindo
 * "DDMMYYYY". Se o Serpro V3 devolver outro formato, a data ficava inválida
 * e o `PutPractitioner` falhava. Aqui aceitamos vários formatos e, se o
 * Serpro não devolver a data, usamos a data digitada no formulário
 * (que acabou de ser validada junto com o CPF).
 */
function toFhirBirthDate(
  serproNascimento: unknown,
  typedBirthDate: unknown,
): string {
  const formats = ["DDMMYYYY", "DD/MM/YYYY", "YYYY-MM-DD", "DD-MM-YYYY"];
  if (serproNascimento) {
    const parsed = dayjs(String(serproNascimento).trim(), formats, true);
    if (parsed.isValid()) return parsed.format("YYYY-MM-DD");
  }
  // A data digitada já foi exigida antes da consulta ao Serpro, então na
  // prática o "" abaixo não deve ocorrer; ele só satisfaz o tipo `string`.
  const typed = dayjs(typedBirthDate as any);
  return typed.isValid() ? typed.format("YYYY-MM-DD") : "";
}

export function InviteDialog({
  isOpen,
  setIsOpen,
  practitioners,
  associationOptions,
  defaultAssociationCnpjDigits,
}: IProps) {
  const { organization } = useOrganizationContext();
  const { refetchPractitionerRole } = usePractitionerRoleContext();

  const [loading, setLoading] = useState(false);
  const methods = useForm({
    defaultValues: {
      ...defaultValues,
      associationCnpj: defaultAssociationCnpjDigits
        ? {
            value: onlyDigits(defaultAssociationCnpjDigits),
            label: useFormatter.formatCNPJ(
              onlyDigits(defaultAssociationCnpjDigits),
            ),
          }
        : null,
    },
  });
  const { access } = useSessionContext();

  const { handleSubmit, control, reset, setValue, watch } = methods;

  const watchedAssociation = useWatch({ control, name: "associationCnpj" });

  // Com CNPJ de associação no convite, não permitimos papel de proprietário (regra de negócio).
  const associationCnpjForRules = useMemo(() => {
    if (defaultAssociationCnpjDigits) {
      return onlyDigits(defaultAssociationCnpjDigits);
    }
    const raw =
      typeof watchedAssociation === "string"
        ? watchedAssociation
        : watchedAssociation?.value;
    return onlyDigits(raw);
  }, [defaultAssociationCnpjDigits, watchedAssociation]);

  const hasAssociationCnpj = Boolean(associationCnpjForRules);

  const roleSelectOptions = useMemo(() => {
    const base = [
      { label: "Proprietário", value: "owner" },
      { label: "Secretariado", value: "manager" },
      { label: "Profissional de saúde", value: "practitioner" },
    ];
    const filtered = hasAssociationCnpj
      ? base.filter((o) => o.value !== "owner")
      : base;
    return filtered.sort((el: any, val: any) => (el.label < val.label ? 1 : -1));
  }, [hasAssociationCnpj]);

  const watchedRoles = watch("role");
  useEffect(() => {
    if (!hasAssociationCnpj || !Array.isArray(watchedRoles)) return;
    const hasOwner = watchedRoles.some((r: any) => r?.value === "owner");
    if (hasOwner) {
      setValue(
        "role",
        watchedRoles.filter((r: any) => r?.value !== "owner"),
        { shouldDirty: true },
      );
    }
  }, [hasAssociationCnpj, watchedRoles, setValue]);

  // const rolesOptions = [
  //   { display: "Profissional de saúde", value: "practitioner" },
  //   { display: "Secretariado", value: "manager" },
  // ];

  // `transformData` (corte por substring de "DDMMYYYY") foi substituído por
  // `toFhirBirthDate`, que aceita os formatos possíveis do Serpro V3.

  const handleClose = () => {
    setIsOpen(false);
    reset(defaultValues);
  };

  const onSubmit = async (data: any) => {
    const practitionerExists = practitioners?.some((el: any) =>
      useFormatter.formatCPF(el?.resource?.id.split("-")[2]) === data.identifier
        ? true
        : false,
    );

    if (!practitionerExists) {
      setLoading(true);
      try {
        const invitedCpfDigits = onlyDigits(data?.identifier);

        // Associação opcional:
        // - Se digitou um CNPJ (freeSolo), validamos se o CPF convidado é sócio.
        // - Se selecionou um CNPJ existente, vinculamos sem validar (isSocio=false).
        const associationCnpjRaw =
          typeof data?.associationCnpj === "string"
            ? data.associationCnpj
            : data?.associationCnpj?.value;
        const associationCnpjDigits = onlyDigits(associationCnpjRaw);
        const associationIsNewCnpj = Boolean(
          associationCnpjDigits &&
          !associationOptions?.some(
            (o) => onlyDigits(o?.cnpjDigits) === associationCnpjDigits,
          ),
        );

        const roleValues: string[] = data?.role?.map((el: any) => el?.value) || [];
        if (associationCnpjDigits && roleValues.includes("owner")) {
          toast.error(
            "Convites vinculados a um CNPJ não podem incluir a função de proprietário.",
          );
          return;
        }

        let associationOrganizationAlias = "";
        let associationIsSocio = false;

        if (associationCnpjDigits) {
          if (associationIsNewCnpj) {
            const validation = await Validate({
              cpf: invitedCpfDigits,
              cnpj: associationCnpjDigits,
            });

            if (validation?.status !== HTTPResponseStatus.Created) {
              toast.error(
                validation?.data || "Não foi possível validar o CNPJ.",
              );
              return;
            }

            associationIsSocio = true;
            const validationData: unknown = validation?.data;
            if (isFhirOrganization(validationData)) {
              associationOrganizationAlias =
                validationData?.alias?.[0] || validationData?.name || "";
            } else {
              // Fallback: não-FHIR. Usamos um alias mínimo e estável.
              associationOrganizationAlias = associationCnpjDigits;
            }
          } else {
            associationIsSocio = false;
            const option = associationOptions?.find(
              (o) => onlyDigits(o?.cnpjDigits) === associationCnpjDigits,
            );
            associationOrganizationAlias =
              option?.organizationAlias || associationCnpjDigits;
          }
        }

        const verifyPracitioner = await GetPractitioner(invitedCpfDigits);
        if (verifyPracitioner) {
          await PutPractitionerRole({
            organizationId: `${oids.cnpj}-${access.organizationId}`,
            organizationDisplay: organization?.name || "",
            practitionerId: verifyPracitioner.id,
            practitionerDisplay: verifyPracitioner?.name?.[0].text,
            role: data?.role?.map((el: any) => el?.value),
            invite: true,
            ...(associationCnpjDigits &&
              associationOrganizationAlias && {
                associationCnpjDigits,
                associationOrganizationAlias,
                associationIsSocio,
              }),
          });
        } else {
          const nascimento = toSerproNascimento(data?.birthDate);
          if (!nascimento) {
            toast.warn("Informe a data de nascimento para validar o CPF.");
            return;
          }
          const invitedData = await SearchCPF(invitedCpfDigits, nascimento);

          // Serpro V3: menor de idade (422) ou bloqueio legal (451).
          // Antes esses casos caíam no catch genérico; agora avisamos e
          // mantemos o modal aberto para o usuário corrigir.
          if (
            invitedData?.status === HTTPResponseStatus.UnprocessableEntity ||
            invitedData?.status === HTTPResponseStatus.ClientError
          ) {
            toast.warn("CPF de menor de idade ou não pode ser validado.");
            return;
          }

          // Qualquer outro status diferente de 200 (ex.: 400 por data de
          // nascimento que não confere, timeout ou erro do BFF).
          if (invitedData?.status !== HTTPResponseStatus.Success) {
            console.error("InviteDialog: falha na consulta de CPF", invitedData);
            toast.warn(
              "Não foi possível validar o CPF. Confira o CPF e a data de nascimento.",
            );
            return;
          }

          if (isSerproCpfRegular(invitedData?.data)) {
            const createdPractitioner = await PutPractitioner({
              identifier: invitedCpfDigits,
              name: invitedData?.data?.nome,
              birthDate: toFhirBirthDate(
                invitedData?.data?.nascimento,
                data?.birthDate,
              ),
            });
            await PutPractitionerRole({
              organizationId: `${oids.cnpj}-${access.organizationId}`,
              organizationDisplay:
                organization?.alias?.[0] || organization?.name || "",
              practitionerId: createdPractitioner.id,
              practitionerDisplay: invitedData?.data?.nome,
              role: data?.role?.map((el: any) => el?.value),
              invite: true,
              ...(associationCnpjDigits &&
                associationOrganizationAlias && {
                  associationCnpjDigits,
                  associationOrganizationAlias,
                  associationIsSocio,
                }),
            });
          } else {
            // CPF encontrado, mas com situação irregular na Receita.
            // Avisamos de forma clara em vez de cair no catch genérico.
            console.error(
              "InviteDialog: CPF com situação irregular",
              invitedData?.data?.situacao,
            );
            toast.warn("CPF com situação irregular na Receita Federal.");
            return;
          }
        }

        toast.success(
          `Convite enviado com sucesso para o profissional de CPF: ${data?.identifier}`,
        );

        refetchPractitionerRole();
        handleClose();

        // O convite já foi criado neste ponto. Se só o e-mail falhar, não
        // mostramos o aviso genérico de erro do convite (era confuso).
        try {
          await SendEmail(
            { email: data.contact },
            data.inviteMessage || "",
            organization?.name,
          );
        } catch (emailErr) {
          console.error("InviteDialog: falha ao enviar e-mail", emailErr);
          toast.warn("Convite criado, mas o e-mail não foi enviado.");
        }
      } catch (err) {
        // Só erros inesperados de rede/FHIR chegam aqui
        // (ex.: PutPractitioner/PutPractitionerRole falharam).
        console.error("InviteDialog: erro inesperado ao criar convite", err);
        toast.info(
          `Atualize a página e verifique se o CPF adicionado consta na aba de convidados`,
        );
      } finally {
        setLoading(false);
      }
    } else {
      handleClose();
      toast.warning("CPF já cadastrado ou aguardando confirmação!");
    }
  };

  return (
    <CommonDialog handleClose={handleClose} isOpen={isOpen}>
      <DialogTitle textAlign="center"> Convidar profissional </DialogTitle>
      <DialogContent>
        <Stack
          width="100%"
          height="100%"
          flex="1"
          justifyContent="center"
          alignItems="center"
          spacing={4}
        >
          <Typography
            variant="body1"
            color="neutral700.main"
            textAlign="center"
            m={{ xs: 2, md: 5 }}
          >
            Informe os dados do profissional que deseja convidar para sua
            clínica
          </Typography>

          <FormTextInput
            required
            name="identifier"
            control={control}
            label="CPF"
            maxLength={14}
            pattern={useRegex.cpf}
            mask={useFormatter.formatCPF}
            autoFocus
          />

          <FormDateInput
            name="birthDate"
            control={control}
            label="Data de nascimento"
          />

          {/* <Stack direction="row" width="100%" spacing={2}>
            <FormAutoComplete
              name="associationCnpj"
              control={control}
              label={
                defaultAssociationCnpjDigits
                  ? "CNPJ selecionado"
                  : "CNPJ (opcional - selecione ou digite)"
              }
              required={false}
              freeSolo={!defaultAssociationCnpjDigits}
              disabled={Boolean(defaultAssociationCnpjDigits)}
              noOptionsText="Digite um CNPJ completo"
              selectOptions={
                Array.isArray(associationOptions)
                  ? associationOptions.map((o) => ({
                      value: onlyDigits(o.cnpjDigits),
                      label: `${o.organizationAlias} (${useFormatter.formatCNPJ(
                        onlyDigits(o.cnpjDigits),
                      )})`,
                    }))
                  : []
              }
            />
          </Stack> */}

          <Stack direction="row" width="100%" spacing={2}>
            <FormTextInput
              required
              type="email"
              name="contact"
              control={control}
              pattern={useRegex.email}
              label="Email"
              autoFocus
            />
          </Stack>

          <Stack direction="row" width="100%" spacing={2}>
            <FormMultipleAutoComplete
              required
              variant="filled"
              control={control}
              // defaultValue={row?.paper}
              label="Função"
              name="role"
              selectOptions={roleSelectOptions}
            />
          </Stack>

          <Stack direction="row" width="100%" spacing={2}>
            <FormTextInput
              multiline={true}
              rows={6}
              name="inviteMessage"
              control={control}
              label="Mensagem"
              autoFocus
            />
          </Stack>
        </Stack>

        <Stack
          minWidth="100%"
          mt={3}
          direction="row"
          justifyContent="space-between"
          spacing={2}
        >
          <FormButtonGroup
            onGoBackButtonClick={handleClose}
            onNextButtonClick={handleSubmit(onSubmit)}
            goBackButtonText="Voltar"
            nextButtonText="Confirmar"
            loading={loading}
          />
        </Stack>
      </DialogContent>
    </CommonDialog>
  );
}
